import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStatus } from "../components/hooks/useAuthStatus";
import AppMenu from "../components/layout/AppMenu";
import ResizableSplit from "../components/layout/ResizableSplit";
import Spinner from "../components/layout/Spinner";
import {
    MatchesPanel,
    PlaylistsPanel,
    ReportsPanel,
} from "../components/layout/TeamPlayerPanels";
import { usingFirestore } from "../components/utils/firestoreData";
import {
    fetchMyMatches,
    matchesForPlayer,
} from "../components/utils/teamPlayerData";
import { createPlayer, fetchAllPlayers } from "../components/utils/teamsPlayers";
import { classNames } from "../components/utils/Utils";
import { useLanguage } from "../context/Language/LanguageContext";

function playerFullName(p) {
  const name = `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim();
  return name.length > 0 ? name : p.nickName ?? "";
}

function PlayersPage() {
  const { firebaseUser, checkingStatus } = useAuthStatus();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const initialPlayerId =
    location.state?.playerId ??
    sessionStorage.getItem("vblive-players-selectedId") ??
    null;

  const [loading, setLoading] = useState(true);
  const [players, setPlayers] = useState([]);
  const [allMatches, setAllMatches] = useState([]);
  const [selectedId, setSelectedId] = useState(initialPlayerId);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    nickName: "",
  });
  const selectedRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!firebaseUser) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const [ps, ms] = await Promise.all([
          fetchAllPlayers(),
          fetchMyMatches(),
        ]);
        if (cancelled) return;
        setPlayers(ps);
        setAllMatches(ms);
        setSelectedId((prev) => prev ?? ps[0]?.id ?? null);
      } catch (e) {
        console.log("Failed to load players", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [firebaseUser]);

  const term = search.trim().toLowerCase();
  const filteredPlayers = term
    ? players.filter((p) =>
        `${playerFullName(p)} ${p.nickName ?? ""}`
          .toLowerCase()
          .includes(term)
      )
    : players;

  const selectedPlayer = useMemo(
    () => players.find((p) => p.id === selectedId) ?? null,
    [players, selectedId]
  );

  const playerMatches = useMemo(
    () => (selectedPlayer ? matchesForPlayer(allMatches, selectedPlayer) : []),
    [allMatches, selectedPlayer]
  );

  const openTeam = (teamId) => {
    if (!teamId) return;
    navigate("/teams", { state: { teamId } });
  };

  // Persist selection so it survives navigating away and back.
  useEffect(() => {
    if (selectedId) sessionStorage.setItem("vblive-players-selectedId", selectedId);
  }, [selectedId]);

  // Scroll the selected player into view (e.g. when arriving from the Teams
  // page via a roster click).
  useEffect(() => {
    if (!loading && selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [selectedId, loading]);

  const reloadPlayers = async () => {
    const ps = await fetchAllPlayers();
    setPlayers(ps);
    return ps;
  };

  const resetForm = () =>
    setForm({ firstName: "", lastName: "", nickName: "" });

  const submitNewPlayer = async () => {
    if (!form.firstName.trim() && !form.lastName.trim()) return;
    setSaving(true);
    try {
      const created = await createPlayer(form);
      if (created) {
        setSearch("");
        await reloadPlayers();
        setSelectedId(created.id);
        setCreating(false);
        resetForm();
      }
    } catch (e) {
      console.log("Failed to create player", e);
    } finally {
      setSaving(false);
    }
  };

  const playerList = (
    <div className="flex h-full flex-col border-r border-base-300">
      <div className="p-2">
        <input
          type="text"
          className="input-generic"
          placeholder={t("Search player...")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {filteredPlayers.length === 0 ? (
          <p className="p-3 text-sm text-base-content/60">
            {t("No players yet.")}
          </p>
        ) : (
          <ul>
            {filteredPlayers.map((p) => (
              <li key={p.id}>
                <button
                  ref={p.id === selectedId ? selectedRef : null}
                  onClick={() => setSelectedId(p.id)}
                  className={classNames(
                    "w-full border-b border-base-200 px-3 py-2 text-left",
                    p.id === selectedId ? "bg-primary/20" : "hover:bg-base-200"
                  )}
                >
                  <div className="truncate text-sm font-medium">
                    {playerFullName(p)}
                  </div>
                  <div className="text-xs text-base-content/60">
                    {p.nickName ? `${p.nickName} · ` : ""}
                    {(p.teamNames ?? []).length} {t("teams")}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );

  const profile = (
    <div className="flex h-full flex-col border-r border-base-300">
      <div className="border-b border-base-300 px-3 py-2">
        <h3 className="text-sm font-semibold">{t("Player")}</h3>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {!selectedPlayer ? (
          <p className="text-sm text-base-content/60">{t("Select a player.")}</p>
        ) : (
          <>
            <div className="text-base font-semibold">
              {playerFullName(selectedPlayer)}
            </div>
            {selectedPlayer.nickName ? (
              <div className="text-sm text-base-content/70">
                {selectedPlayer.nickName}
              </div>
            ) : null}
            <div className="mt-3 text-xs font-semibold uppercase text-base-content/60">
              {t("Teams")}
            </div>
            {(selectedPlayer.teamNames ?? []).length === 0 ? (
              <p className="text-sm text-base-content/60">{t("No teams.")}</p>
            ) : (
              <div className="mt-1 flex flex-wrap gap-1">
                {(selectedPlayer.teamNames ?? []).map((name, idx) => (
                  <button
                    key={idx}
                    className="badge badge-ghost rounded-none hover:badge-primary"
                    onClick={() =>
                      openTeam((selectedPlayer.teamIds ?? [])[idx])
                    }
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  const detailPanels = (
    <div className="flex h-full min-w-0 flex-col gap-2 p-2">
      <MatchesPanel matches={playerMatches} t={t} />
      <PlaylistsPanel scope="player" refId={selectedPlayer?.id} t={t} />
      <ReportsPanel
        scope="player"
        refId={selectedPlayer?.id}
        matches={playerMatches}
        reportLabel={selectedPlayer ? playerFullName(selectedPlayer) : null}
        t={t}
      />
    </div>
  );

  const rightSide = selectedPlayer ? (
    <ResizableSplit
      storageKey="vblive-players-profile-w"
      defaultWidth={240}
      min={180}
      max={420}
      left={profile}
      right={detailPanels}
    />
  ) : (
    <div className="flex h-full items-center justify-center text-base-content/60">
      {t("Select a player.")}
    </div>
  );

  return (
    <div className="mt-2 flex h-[90vh] flex-col">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AppMenu />
          <h2 className="text-lg font-semibold">{t("Players")}</h2>
        </div>
        <div className="flex gap-1">
          <button
            className="btn btn-sm btn-primary"
            onClick={() => setCreating(true)}
            disabled={!usingFirestore()}
          >
            {t("New player")}
          </button>
          <button className="btn btn-sm btn-ghost" onClick={() => navigate(-1)}>
            {t("Back")}
          </button>
        </div>
      </div>
      {loading || checkingStatus ? (
        <Spinner />
      ) : !usingFirestore() ? (
        <div className="p-6 text-center text-base-content/60">
          {t(
            "Your teams and players will appear here after your data is migrated."
          )}
        </div>
      ) : (
        <div className="min-h-0 flex-1 rounded-md border border-base-300">
          <ResizableSplit
            storageKey="vblive-players-list-w"
            defaultWidth={240}
            min={180}
            max={480}
            left={playerList}
            right={rightSide}
          />
        </div>
      )}

      {creating && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="mb-3 text-lg font-bold">{t("New player")}</h3>
            <div className="flex flex-col gap-2">
              <input
                type="text"
                className="input-generic"
                placeholder={t("First name")}
                value={form.firstName}
                onChange={(e) =>
                  setForm({ ...form, firstName: e.target.value })
                }
              />
              <input
                type="text"
                className="input-generic"
                placeholder={t("Last name")}
                value={form.lastName}
                onChange={(e) =>
                  setForm({ ...form, lastName: e.target.value })
                }
              />
              <input
                type="text"
                className="input-generic"
                placeholder={t("Nickname")}
                value={form.nickName}
                onChange={(e) =>
                  setForm({ ...form, nickName: e.target.value })
                }
              />
            </div>
            <div className="modal-action">
              <button
                className="btn btn-sm"
                onClick={() => {
                  setCreating(false);
                  resetForm();
                }}
              >
                {t("Cancel")}
              </button>
              <button
                className="btn btn-sm btn-primary"
                onClick={submitNewPlayer}
                disabled={
                  saving ||
                  (!form.firstName.trim() && !form.lastName.trim())
                }
              >
                {saving ? (
                  <span className="loading loading-spinner loading-xs"></span>
                ) : (
                  t("Create")
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PlayersPage;
